import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { UploadFiles } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useState } from "react";

import {
    type SourceOfFundsPayload,
    submitSourceOfFunds
} from "@/services/clientOnboarding";
import { uploadOnboardingDocument } from "@/services/clientOnboardingDocuments";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useFundsNarrativeFormState } from "../onboardingFormStateContext";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE } from "../panel/uploadConstraints";

const FUNDS_DOC_EXAMPLES: string[] = [
    "Audited or unaudited financial statements",
    "Recent bank statements showing balances or inflows",
    "Capital documentation (term sheets, capital call notices, token sale agreements)",
    "Treasury reports or on-chain treasury attestations",
    "Recent third-party audit report"
];

/**
 * Supporting Documents (Optional) — slot 5.2. Attaching documents
 * strengthens review and can speed approval. None are required at
 * submission, but extended due diligence may request one or more if
 * compliance flags risk.
 */
export function SupportingDocsForm({
    slotTitle,
    onSubmit
}: {
    slotTitle: string;
    onSubmit: (payload: unknown) => void;
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    const persistedStep = context?.stepsByName.source_of_funds;
    // Whether a supporting doc is attached lives on the shared funds
    // narrative form-state slice so it survives navigation and stays in
    // sync with FundsNarrativeForm's view of the same step. Hydration is
    // handled once inside OnboardingFormStateProvider.
    const [fundsState, setFundsState] = useFundsNarrativeFormState();
    const supportingDocsCount = fundsState.supportingDocAttached ? 1 : 0;
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const handleSelect = async (files: FileList | null): Promise<void> => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!context) {
            setFundsState((prev) => ({ ...prev, supportingDocAttached: true }));
            onSubmit({ supportingDocsCount: 1 });
            return;
        }
        setUploading(true);
        setUploadError(null);
        try {
            const doc = await uploadOnboardingDocument({
                applicationUuid: context.application.uuid,
                purpose: "supporting_document",
                file
            });
            const existing = persistedStep?.payload as
                | Partial<SourceOfFundsPayload>
                | undefined;
            // Source of Funds requires both narrative fields. If the
            // narrative hasn't been saved yet, skip the BE submit and
            // just persist the upload — the doc row exists, the user
            // can come back to the narrative slot.
            if (existing?.sourceCategory && existing?.sourceDescription) {
                const payload: SourceOfFundsPayload = {
                    sourceCategory: existing.sourceCategory,
                    sourceDescription: existing.sourceDescription,
                    supportingDocumentUuid: doc.uuid
                };
                const result = await submitSourceOfFunds(
                    context.application.uuid,
                    payload
                );
                context.applyStepUpdate(result);
            }
            setFundsState((prev) => ({ ...prev, supportingDocAttached: true }));
            onSubmit({ supportingDocumentUuid: doc.uuid });
        } catch (err) {
            setUploadError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Supporting document was not uploaded. Choose the file again and retry."
                )
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title={slotTitle}
                className="pb-4 border-b border-line-base"
            />

            <Text
                variant="small"
                className="block leading-relaxed text-foreground-muted pt-4"
            >
                Attach any supporting documents for your source of funds.
            </Text>

            <Card border={false} className="px-5 py-5 mt-2">
                <Heading
                    title="Examples of helpful documents"
                    titleVariant="h3"
                    className="pb-3"
                />
                <ul className="space-y-1.5">
                    {FUNDS_DOC_EXAMPLES.map((doc) => (
                        <li key={doc} className="flex items-center gap-2">
                            <IndicationUnit
                                variant="status"
                                state="disabled"
                                size={6}
                            />
                            <Text
                                variant="small"
                                className="leading-none text-foreground-base"
                            >
                                {doc}
                            </Text>
                        </li>
                    ))}
                </ul>
            </Card>

            {/* Multi-file: the slot stays open while a file is in flight so
                more can be queued. The attached list lives in the Card above,
                so this control renders the zone and the failure only. */}
            <div className="pt-12">
                <UploadFiles
                    className="max-w-sm mx-auto"
                    size="sm"
                    accept={DOCUMENT_ACCEPT}
                    maxSize={DOCUMENT_MAX_SIZE}
                    multiple
                    state={
                        uploadError
                            ? "error"
                            : uploading
                              ? "uploading"
                              : "empty"
                    }
                    error={uploadError ?? undefined}
                    onSelect={(files) => void handleSelect(files)}
                />
                {supportingDocsCount > 0 && !uploadError && (
                    <Text
                        variant="label"
                        className="block leading-snug text-foreground-muted text-center pt-1"
                    >
                        {supportingDocsCount} document
                        {supportingDocsCount === 1 ? "" : "s"} attached
                    </Text>
                )}
            </div>
        </motion.div>
    );
}
